import { Box } from '@cloudscape-design/components';
import { useEffect, useMemo, useState } from 'react';
import ReactSlider from 'react-slider';
import './MultiBottleSlider.scss';

type BottleSliderProps = {
  totalLiters: number;
  sizes?: number[]; // e.g. [0.35, 0.5, 0.75]
  onChange?: (value: {
    bottleCounts: { [size: number]: number };
    remainder: number;
    allocated: { [size: number]: number };
  }) => void;
};

const StackedBottleSlider = ({ totalLiters, sizes = [0.35, 0.5, 0.75], onChange }: BottleSliderProps) => {
  const initPositions = (arr: number[]) =>
    arr.length === 1 ? [100] : arr.map((_, i) => (i === arr.length - 1 ? 85 : i * 20));
  const [positions, setPositions] = useState<number[]>(initPositions(sizes));

  // Re-init when sizes change
  useEffect(() => {
    setPositions(initPositions(sizes));
  }, [sizes]);

  const isSingle = sizes.length === 1;

  // Compute allocation
  const { allocated, bottleCounts, remainder } = useMemo(() => {
    const allocated: Record<number, number> = {};
    const bottleCounts: Record<number, number> = {};
    let last = 0;
    sizes.forEach((size, i) => {
      const pct = (positions[i] ?? 0) - last;
      const vol = (pct / 100) * totalLiters;
      allocated[size] = vol;
      bottleCounts[size] = Math.floor(vol / size);
      last = positions[i] ?? last;
    });
    const usedVolume = Object.entries(bottleCounts).reduce(
      (sum, [size, count]) => sum + Number(size) * count,
      0,
    );
    const remainder = totalLiters - usedVolume;
    return { allocated, bottleCounts, remainder };
  }, [positions, sizes, totalLiters]);

  // Notify parent
  useEffect(() => {
    onChange?.({ bottleCounts, remainder, allocated });
  }, [allocated, bottleCounts, remainder, onChange]);

  // Normalize slider value + onChange for single vs multi
  const sliderValue: number | number[] = isSingle ? positions[0] : positions;

  const handleChange = (vals: number | number[]) => {
    const arr = Array.isArray(vals) ? vals : [vals];
    setPositions(arr);
  };

  return (
    <Box>
      <div className="slider-labels">
        {sizes.map((size) => (
          <div key={size}>
            {size}L: {bottleCounts[size]} bottles ({(allocated[size] || 0).toFixed(2)}L)
          </div>
        ))}
        <div>Remaining: {remainder.toFixed(2)}L</div>
      </div>

      <ReactSlider
        className="multi-slider"
        thumbClassName="multi-thumb"
        trackClassName="multi-track"
        value={sliderValue}
        min={0}
        max={100}
        step={1}
        // These two options are only meaningful for multi-thumb sliders:
        withTracks={true}
        minDistance={0}
        onChange={handleChange}
        renderThumb={(props, state) => <div {...props}>{sizes[state.index ?? 0]}L</div>}
      />
    </Box>
  );
};

export default StackedBottleSlider;
